"use client";

import { useState, useEffect, useMemo } from "react";
import { AlertCircle, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface AktualnaSprava {
  id: string;
  text: string;
  typ: "info" | "zranenie" | "dolezite";
  datum: string;
}

const aktualizacie: AktualnaSprava[] = [
  {
    id: "1",
    text: "Slovensko vyhralo nad Fínskom 4:1 v úvodnom zápase hokejového turnaja ZOH 2026.",
    typ: "dolezite",
    datum: "2026-02-11",
  },
  {
    id: "2",
    text: "Slovensko zdolalo Taliansko 3:2 a má 2–0 v skupine B na ZOH 2026.",
    typ: "dolezite",
    datum: "2026-02-13",
  },
  {
    id: "3",
    text: "Slovensko hrá dnes so Švédskom o priame postupové miesto do štvrťfinále.",
    typ: "info",
    datum: "2026-02-14",
  },
  {
    id: "4",
    text: "Juraj Slafkovský a Adam Ružička sa presadzujú v olympijskom hokejovom turnaji.",
    typ: "info",
    datum: "2026-02-13",
  },
];

const TYPOVY_FARBY = {
  info: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  zranenie: "bg-red-500/10 text-red-600 border-red-500/20",
  dolezite: "bg-green-500/10 text-green-600 border-green-500/20",
};

const TYPOVY_IKONY = {
  info: TrendingUp,
  zranenie: AlertCircle,
  dolezite: TrendingUp,
};

export function AktualnyTicker() {
  const [aktualnaIndex, setAktualnaIndex] = useState(0);

  const zoradeneAktualizacie = useMemo(
    () =>
      [...aktualizacie].sort(
        (a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime(),
      ),
    [],
  );

  useEffect(() => {
    if (zoradeneAktualizacie.length <= 1) return;

    const interval = setInterval(() => {
      setAktualnaIndex((prevIndex) =>
        prevIndex === zoradeneAktualizacie.length - 1 ? 0 : prevIndex + 1,
      );
    }, 5000); // Zmena každých 5 sekúnd

    return () => clearInterval(interval);
  }, [zoradeneAktualizacie.length]);

  if (zoradeneAktualizacie.length === 0) return null;

  const aktualnaSprava = zoradeneAktualizacie[aktualnaIndex];
  const Ikona = TYPOVY_IKONY[aktualnaSprava.typ];
  const farba = TYPOVY_FARBY[aktualnaSprava.typ];

  return (
    <div className="border-b border-muted/20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 sm:gap-3 py-2 overflow-hidden">
          <div className="flex items-center gap-2 shrink-0">
            <div
              className={cn(
                "w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center",
                farba,
              )}
            >
              <Ikona className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
            </div>
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider shrink-0 hidden sm:block">
              Posledná zmena
            </span>
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider shrink-0 sm:hidden">
              Novinky
            </span>
          </div>

          <div className="relative overflow-hidden flex-1 min-w-0">
            <div
              className="whitespace-nowrap text-xs sm:text-sm text-muted-foreground animate-slide-in truncate"
              key={aktualnaIndex}
            >
              {aktualnaSprava.text}
            </div>
          </div>

          {zoradeneAktualizacie.length > 1 && (
            <div className="flex items-center gap-1 shrink-0">
              {zoradeneAktualizacie.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full transition-all duration-300",
                    index === aktualnaIndex
                      ? "bg-primary"
                      : "bg-muted-foreground/30",
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
